import { forwardRef, useState, useRef, useImperativeHandle, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { useToast } from '@shared/hooks/use-toast';
import { useCreateMapping, useCourierIncidentKinds, useIncidentTypes, useCouriers } from '../../hooks';
import { CreateMappingPayload } from '../../models/Mapping';
import { useQueryClient } from '@tanstack/react-query';

export type CreateMappingModalRef = ModalCenterRef;

export const CreateMappingModal = forwardRef<CreateMappingModalRef>((_, ref) => {
  const { t } = useTranslation('mappingsModule');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: couriersData = [], isLoading: isLoadingCouriers } = useCouriers();

  // Adaptar los couriers al formato necesario para el Select
  const couriers = useMemo(() => {
    return couriersData.map((courier: { id: number; name: string }) => ({
      label: courier.name,
      value: courier.id.toString(),
    }));
  }, [couriersData]);
  const { data: incidentTypes = [], isLoading: isLoadingTypes } = useIncidentTypes();
  const createMutation = useCreateMapping();

  const [courierId, setCourierId] = useState<string>('');
  const [courierKindId, setCourierKindId] = useState<string>('');
  const [incidentTypeId, setIncidentTypeId] = useState<string>('none');

  // Cargar tipos del courier cuando cambia el courier (solo si no es "none")
  const { data: currentCourierKinds = [], isLoading: isLoadingCurrentCourierKinds } = useCourierIncidentKinds(
    courierId && courierId !== 'none' ? parseInt(courierId, 10) : null,
  );

  const internalModalRef = useRef<ModalCenterRef>(null);
  const courierSelectRef = useRef<HTMLButtonElement>(null);

  const resetForm = () => {
    setCourierId('');
    setCourierKindId('');
    setIncidentTypeId('none');
  };

  // 🎯 Resetear formulario cuando se abre el modal y exponer métodos del modal
  useImperativeHandle(ref, () => ({
    openModal: () => {
      resetForm();
      internalModalRef.current?.openModal();
      // Enfocar el campo Courier después de que el modal se abra
      setTimeout(() => {
        courierSelectRef.current?.focus();
      }, 100);
    },
    closeModal: () => {
      internalModalRef.current?.closeModal();
    },
  }));

  const handleCancel = () => {
    internalModalRef.current?.closeModal();
    resetForm();
  };

  const handleCreate = async () => {
    // Validar que si hay courier seleccionado, el tipo externo es obligatorio
    if (courierId && courierId !== 'none' && !courierKindId) {
      toast({
        title: t('form.error.title'),
        description: t('form.error.fill_required'),
        variant: 'destructive',
      });
      return;
    }

    // Si no hay courier o es "none", no se puede crear (tipo externo es obligatorio)
    if (!courierId || courierId === 'none') {
      toast({
        title: t('form.error.title'),
        description: t('form.error.select_courier'),
        variant: 'destructive',
      });
      return;
    }

    try {
      const payload: CreateMappingPayload = {
        courier_incident_kind_id: parseInt(courierKindId, 10),
        incident_type_id: incidentTypeId && incidentTypeId !== 'none' ? parseInt(incidentTypeId, 10) : null,
      };

      const response = await createMutation.mutateAsync(payload);

      if (response.success) {
        toast({
          title: t('form.success.title'),
          description: response.message || t('form.success.create'),
          variant: 'default',
        });
        // 🎯 Invalidar queries para refrescar la lista
        queryClient.invalidateQueries({ queryKey: ['mappings'] });
        resetForm();
        handleCancel();
      } else {
        toast({
          title: t('form.error.title'),
          description: response.message || t('form.error.unexpected'),
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.log('🔍 CreateMappingModal - Error:', error);
      toast({
        title: t('form.error.title'),
        description: error?.message || t('form.error.unexpected'),
        variant: 'destructive',
      });
    }
  };

  const isLoading = createMutation.isPending;

  return (
    <ModalCenter ref={internalModalRef} title={t('form.create_title')} type="default">
      <div className="space-y-4">
        {/* Courier - Requerido */}
        <div>
          <label className="text-sm font-medium text-title mb-2 block">{t('form.courier')} *</label>
          <Select
            value={courierId}
            onValueChange={(value) => {
              setCourierId(value);
              // Resetear courier kind cuando cambia el courier
              setCourierKindId('');
            }}
            disabled={isLoadingCouriers}
          >
            <SelectTrigger ref={courierSelectRef} data-testid="modal-courier-select">
              <SelectValue placeholder={t('form.select_courier')} />
            </SelectTrigger>
            <SelectContent className="max-w-[var(--radix-select-trigger-width)]">
              <SelectItem value="none" className="whitespace-normal">
                {t('form.none_courier')}
              </SelectItem>
              {couriers.length > 0
                ? couriers.map((courier: { label: string; value: string }) => (
                    <SelectItem key={courier.value} value={courier.value} className="whitespace-normal">
                      <span className="whitespace-normal break-words">{courier.label}</span>
                    </SelectItem>
                  ))
                : !isLoadingCouriers && (
                    <div className="px-2 py-1.5 text-sm text-complementary-text text-center">
                      {t('form.no_results')}
                    </div>
                  )}
            </SelectContent>
          </Select>
        </div>

        {/* Tipo externo (courier incident kind) - Requerido, solo visible cuando hay courier seleccionado (no "none") */}
        {courierId && courierId !== 'none' && (
          <div>
            <label className="text-sm font-medium text-title mb-2 block">{t('form.external_type')} *</label>
            <Select
              value={courierKindId}
              onValueChange={setCourierKindId}
              disabled={isLoadingCurrentCourierKinds || !courierId || courierId === 'none'}
            >
              <SelectTrigger data-testid="modal-external-type-select">
                <SelectValue placeholder={t('form.select_external_type')} />
              </SelectTrigger>
              <SelectContent className="max-w-[var(--radix-select-trigger-width)]">
                {currentCourierKinds.length > 0
                  ? currentCourierKinds.map((kind) => (
                      <SelectItem key={kind.id} value={kind.id.toString()} className="whitespace-normal">
                        <span className="whitespace-normal break-words">{kind.external_label}</span>
                      </SelectItem>
                    ))
                  : !isLoadingCurrentCourierKinds && (
                      <div className="px-2 py-1.5 text-sm text-complementary-text text-center">
                        {t('form.no_results')}
                      </div>
                    )}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Tipo interno - Opcional */}
        <div>
          <label className="text-sm font-medium text-title mb-2 block">{t('form.internal_type')}</label>
          <Select value={incidentTypeId} onValueChange={setIncidentTypeId} disabled={isLoadingTypes}>
            <SelectTrigger data-testid="modal-internal-type-select">
              <SelectValue placeholder={t('form.select_internal_type')} />
            </SelectTrigger>
            <SelectContent className="max-w-[var(--radix-select-trigger-width)]">
              <SelectItem value="none" className="whitespace-normal">
                {t('form.none')}
              </SelectItem>
              {incidentTypes.length > 0
                ? incidentTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id.toString()} className="whitespace-normal">
                      <span className="whitespace-normal break-words">{type.label}</span>
                    </SelectItem>
                  ))
                : !isLoadingTypes && (
                    <div className="px-2 py-1.5 text-sm text-complementary-text text-center">
                      {t('form.no_results')}
                    </div>
                  )}
            </SelectContent>
          </Select>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 mt-6 pt-4">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
            data-testid="modal-cancel-button"
            size="lg"
          >
            {t('form.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={isLoading || !courierId || courierId === 'none' || !courierKindId}
            data-testid="modal-submit-button"
            size="lg"
          >
            {isLoading ? t('form.saving') : t('form.create')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

CreateMappingModal.displayName = 'CreateMappingModal';
